import { useNavigate } from "react-router-dom";
import { useState, useMemo, useEffect } from "react";
import { useTable, useSortBy, usePagination } from "react-table";
import Loader from "../../../../reusableUI/loader";
import { capitalizeFirstLetter } from "../../../../utils/Functions";
import moment from "moment";
import {
  getAssignedStaffList,
  getStandByStaffs,
  assignStaffs,
  actingHCPChange,
  timeSlotChange,
  deleteVisit,
} from "../../../../Service/partnerAPI";
import { getLookupData } from "../../../../Service/lookup";
import { CommonImages } from "../../../../reusableUI/images";
import LiveTrackingMap from "../../../../reusableUI/LiveTrackingMap";
import { toast } from "react-toastify";
import PatientHistory from "./patientHistory";

const START_DATE_REQUIRED_TOAST_ID = "booking-start-date-required";

const SortIcon = ({ isSorted, isSortedDesc }) => {
  if (!isSorted) {
    return (
      <svg
        className="h-3 w-3 ml-1 text-gray-400"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M5 12l5 5 5-5H5zm10-4L10 3 5 8h10z" />
      </svg>
    );
  }

  if (isSortedDesc) {
    return (
      <svg
        className="h-3 w-3 ml-1 text-blue-600"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M5 8l5 5 5-5H5z" />
      </svg>
    );
  }

  return (
    <svg
      className="h-3 w-3 ml-1 text-blue-600"
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      <path d="M15 12l-5-5-5 5h10z" />
    </svg>
  );
};

const getDutyStatus = (appointment, currentDate = new Date()) => {
  if (!appointment) return "Unknown";

  const { status, paymentInfo, dutyInfo } = appointment;

  const statusKey = String(status || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

  // ✅ If overall status is endDuty => Completed
  if (statusKey === "endduty") return "Completed";
  if (statusKey === "completed") return "Completed";

  const today = moment(currentDate).format("YYYY-MM-DD");
  const currentDuty = dutyInfo?.find((duty) => moment(duty.date).format("YYYY-MM-DD") === today);

  if (statusKey === "rejected") return "Cancelled";
  if (statusKey === "accepted") return "Assigned";
  if (statusKey === "pending") return "Unassigned";

  if (currentDuty) {
    // ✅ If duty ended => Completed (instead of "Duty Ended")
    if (currentDuty.endDuty?.verified) return "Completed";
    if (currentDuty.startDuty?.verified) return "Duty Started";
    if (String(currentDuty.liveStatus || "").toLowerCase() === "arrived") return "Started";
  }

  if (statusKey === "paymentpending") return "Payment Pending";

  if (
    paymentInfo?.paidInfo?.paid === false &&
    statusKey !== "pending" &&
    statusKey !== "accepted" &&
    statusKey !== "rejected"
  ) {
    return "Payment Pending";
  }

  return "Unknown";
};

const StatusBadge = ({ status }) => {
  const statusStyles = {
    Unassigned: {
      text: "Unassigned",
      textColor: "text-red-600",
    },
    Requested: {
      text: "Requested",
      textColor: "text-orange-600",
    },
    "Payment Pending": {
      text: "Payment Pending",
      textColor: "text-orange-600",
    },
    Assigned: {
      text: "Assigned",
      textColor: "text-green-600",
      bgColor: "bg-green-50",
      borderColor: "border-green-200",
    },
    Started: {
      text: "Started",
      textColor: "text-blue-600",
    },
    "Duty Started": {
      text: "Duty Started",
      textColor: "text-purple-600",
    },
    "Duty Ended": {
      text: "Duty Ended",
      textColor: "text-gray-600",
    },
    Completed: {
      text: "Completed",
      textColor: "text-teal-600",
    },
    Cancelled: {
      text: "Cancelled",
      textColor: "text-red-700",
    },
    Unknown: {
      text: "Unknown",
      textColor: "text-gray-500",
    },
  };

  const currentStyle = statusStyles[status] || statusStyles.Unknown;

  return (
    <span
      className={`
        inline-flex items-center justify-center
        px-2.5 py-1
        text-[11px] font-medium font-poppins
        rounded-full
        ${currentStyle.textColor}
        ${currentStyle.bgColor || ""}
        ${currentStyle.borderColor || ""}
      `}
    >
      {currentStyle.text}
    </span>
  );
};

const getServiceDayLabel = (day) => {
  if (day == null) return "";
  const d = Number(day);
  if (Number.isNaN(d)) return "";
  const j = d % 10;
  const k = d % 100;
  let suffix = "th";
  if (j === 1 && k !== 11) suffix = "st";
  else if (j === 2 && k !== 12) suffix = "nd";
  else if (j === 3 && k !== 13) suffix = "rd";
  return `${d}${suffix}`;
};

const getCurrentServiceDayLabel = (visit) => {
  const today = moment().format("YYYY-MM-DD");
  const dutyToday = visit?.dutyInfo?.find((d) => d.date === today);
  if (dutyToday?.day != null) return getServiceDayLabel(dutyToday.day);
  return "-";
};

const isDutyCompleted = (duty) => {
  if (!duty) return false;
  if (
    typeof duty.serviceStatus === "string" &&
    duty.serviceStatus.toLowerCase() === "completed"
  ) {
    return true;
  }
  if (duty.endDuty?.verified) return true;
  return false;
};

const PHYSIO_TIME_SLOTS = [
  "06:00 AM to 07:00 AM",
  "07:00 AM to 08:00 AM",
  "08:00 AM to 09:00 AM",
  "09:00 AM to 10:00 AM",
  "10:00 AM to 11:00 AM",
  "11:00 AM to 12:00 PM",
  "12:00 PM to 01:00 PM",
  "01:00 PM to 02:00 PM",
  "02:00 PM to 03:00 PM",
  "03:00 PM to 04:00 PM",
  "04:00 PM to 05:00 PM",
  "05:00 PM to 06:00 PM",
  "06:00 PM to 07:00 PM",
  "07:00 PM to 08:00 PM",
  "08:00 PM to 09:00 PM",
];

const PHYSIO_MANUAL_ASSIGN_SLOTS = (() => {
  const slots = [];
  for (let hour = 9; hour < 21; hour++) {
    const start = moment({ hour, minute: 0 });
    const end = moment({ hour: hour + 1, minute: 0 });
    slots.push(`${start.format("hh:mm A")} - ${end.format("hh:mm A")}`);
  }
  return slots;
})();

const ONE_TIME_MANUAL_SLOTS = (() => {
  const slots = [];
  for (let hour = 9; hour <= 21; hour++) {
    const start = moment({ hour, minute: 0 });
    slots.push(start.format("hh:mm A"));
  }
  return slots;
})();

const getStaffDisplayName = (staff) => {
  if (!staff) return "";
  const info = staff.personalInfo?.basicInfo?.nameInfo || {};
  const title = info.title || "";
  const fullName = info.fullName || "";
  return title
    ? `${title}. ${fullName || ""}`.trim()
    : (fullName || "").trim();
};

const parseSelectedSlot = (slot) => {
  const raw = String(slot || "").trim();
  if (!raw) return { startTimeStr: "", endTimeStr: "" };
  const normalized = raw.includes(" - ") ? raw.replace(" - ", " to ") : raw;
  const [from, to] = normalized.split(/\s+to\s+/i).map((s) => s.trim());
  return {
    startTimeStr: from || "",
    endTimeStr: to || "",
  };
};

const toTimeRangeLabel = (slot) => {
  const { startTimeStr, endTimeStr } = parseSelectedSlot(slot);
  if (startTimeStr && endTimeStr) {
    return `${startTimeStr} to ${endTimeStr}`;
  }
  return String(slot || "").trim();
};

const WEEK_DAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const normalizeSessionFrequency = (value) => {
  const token = String(value || "")
    .toLowerCase()
    .replace(/\s+/g, "");
  if (!token) return "";
  if (token.includes("alternative")) return "alternative";
  if (token.includes("twice")) return "twice";
  if (token.includes("thrice") || token.includes("three")) return "thrice";
  if (token.includes("daily") || token.includes("everyday")) return "daily";
  return "";
};

const buildSessionDurationDays = ({
  startDate,
  sessionCount,
  frequency,
  excludedDays = [],
  selectedWeekDays = [],
}) => {
  const start = moment(startDate, "YYYY-MM-DD", true);
  if (!start.isValid()) return [];

  const rawSessionCount = Number(sessionCount);
  const safeSessionCount =
    Number.isFinite(rawSessionCount) && rawSessionCount > 0
      ? Math.floor(rawSessionCount)
      : 0;

  if (safeSessionCount <= 0) return [];

  const excludedSet = new Set(
    (Array.isArray(excludedDays) ? excludedDays : []).map((day) =>
      String(day || "").trim()
    )
  );

  const weekDaySet = new Set(
    (Array.isArray(selectedWeekDays) ? selectedWeekDays : []).map((day) =>
      String(day || "").trim()
    )
  );

  if (excludedSet.size >= 7) return [];
  if ((frequency === "twice" || frequency === "thrice") && weekDaySet.size === 0)
    return [];

  const result = [];
  let cursor = start.clone();
  let step = 0;
  let safetyCounter = 0;

  while (result.length < safeSessionCount && safetyCounter < 3000) {
    const weekDay = cursor.format("dddd");
    let include = false;

    if (frequency === "alternative") {
      include = step % 2 === 0 && !excludedSet.has(weekDay);
    } else if (frequency === "twice" || frequency === "thrice") {
      include = weekDaySet.has(weekDay);
    } else {
      include = !excludedSet.has(weekDay);
    }

    if (include) {
      result.push(cursor.format("YYYY-MM-DD"));
    }

    cursor.add(1, "day");
    step += 1;
    safetyCounter += 1;
  }

  return result;
};

const CALENDAR_WEEK_HEADERS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const buildMonthCalendarCells = (monthDate) => {
  const monthStart = monthDate.clone().startOf("month");
  const calendarStart = monthStart.clone().startOf("week");
  const calendarEnd = monthDate.clone().endOf("month").endOf("week");
  const cells = [];
  const cursor = calendarStart.clone();

  while (cursor.isSameOrBefore(calendarEnd, "day")) {
    cells.push({
      dateKey: cursor.format("YYYY-MM-DD"),
      label: cursor.format("D"),
      inCurrentMonth: cursor.month() === monthDate.month(),
    });
    cursor.add(1, "day");
  }

  return cells;
};

const formatLabelToken = (value = "") => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
};

const normalizeDutyTypeToken = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

const parseShiftType = (shiftType = "") => {
  const parts = String(shiftType || "")
    .split(",")
    .map((item) => String(item || "").trim())
    .filter(Boolean);

  return {
    shift: String(parts[0] || "").toLowerCase(),
    hours: String(parts[1] || "").toUpperCase(),
    parts,
  };
};

const isTwentyFourBySevenShiftType = (shiftType = "") => {
  const { shift, hours } = parseShiftType(shiftType);
  const normalizedShift = String(shift || "")
    .toLowerCase()
    .replace(/\s+/g, "");
  const normalizedHours = String(hours || "")
    .toUpperCase()
    .replace(/\s+/g, "");

  const isTwentyFourBySevenShift =
    normalizedShift === "24/7" ||
    normalizedShift === "24x7" ||
    normalizedShift === "247";
  const isTwentyFourHourDuty =
    normalizedHours === "24" || normalizedHours === "24H";

  return isTwentyFourBySevenShift && isTwentyFourHourDuty;
};

const getRegularLookupTypeByShift = (shiftType = "") => {
  const { shift, hours } = parseShiftType(shiftType);
  if (shift === "morning" && hours === "12H") return "morningSHift12Data";
  if (shift === "morning" && hours === "9H") return "morningShift9Data";
  if (shift === "night" && hours === "12H") return "nightShift12Data";
  return "";
};

const extractLookupSlotValues = (lookupItem) => {
  const options = [];

  if (Array.isArray(lookupItem?.values)) {
    lookupItem.values.forEach((item) => {
      const text = String(
        item?.value ?? item?.label ?? item?.name ?? item?.title ?? item ?? ""
      ).trim();
      if (text) options.push(text);
    });
  }

  const fallback = String(lookupItem?.value ?? lookupItem?.label ?? "").trim();
  if (fallback) options.push(fallback);

  return options;
};

const getDutyTypeDisplay = (visit) => {
  const role = String(visit?.specialistInfo?.role || "").trim().toLowerCase();
  const dutyTypeRaw = String(visit?.dutyType || "").trim();
  const dutyType = normalizeDutyTypeToken(dutyTypeRaw);

  if (role === "physiotherapist") return "Session";

  if (role === "nurse") {
    if (dutyType === "onetime") return "One Time";

    if (dutyType === "regular") {
      const { shift, hours } = parseShiftType(visit?.visitInfo?.shiftType || "");
      if (shift && hours) return `${formatLabelToken(shift)}, ${hours}`;
      if (shift) return formatLabelToken(shift);
      return "Regular";
    }
  }

  if (dutyType === "onetime") return "One Time";

  const shiftTypeRaw = String(visit?.visitInfo?.shiftType || "").trim();
  if (shiftTypeRaw && dutyTypeRaw) return `${shiftTypeRaw}, ${dutyTypeRaw}`;
  if (shiftTypeRaw) return shiftTypeRaw;
  if (dutyTypeRaw) return dutyTypeRaw;
  return "-";
};

const getLocationDisplay = (visit) => {
  const cityRaw = String(visit?.visitInfo?.addressInfo?.city || "").trim();
  const districtRaw = String(visit?.visitInfo?.addressInfo?.district || "").trim();

  const city = cityRaw ? formatLabelToken(cityRaw) : "";
  const district = districtRaw ? formatLabelToken(districtRaw) : "";

  if (city && district) return `${city}, ${district}`;
  if (city) return city;
  if (district) return district;
  return "-";
};

const computeEndDateFromStartAndDays = (startDate, days) => {
  const parsed = moment(startDate, "YYYY-MM-DD", true);
  if (!parsed.isValid()) return "";
  const dayCount = Number(days);
  const safeDays = Number.isFinite(dayCount) && dayCount > 0 ? dayCount : 1;
  return parsed.add(safeDays - 1, "day").format("YYYY-MM-DD");
};

const PatientList = ({ searchTerm }) => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [visitList, setVisitList] = useState([]);
  const [paginationPage, setPaginationPage] = useState(0);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [assignStaffList, setAssignStaffList] = useState([]);
  const [assessmentStaff, setAssessmentStaff] = useState("");
  const [selectedMainStaff, setSelectedMainStaff] = useState(null);
  const [selectedStandby1, setSelectedStandby1] = useState(null);
  const [selectedStandby2, setSelectedStandby2] = useState(null);
  const [isEditingStaff, setIsEditingStaff] = useState(false);
  const [editingDutyIndex, setEditingDutyIndex] = useState(null);
  const [changingDutyStaff, setChangingDutyStaff] = useState({});
  const [showTracking, setShowTracking] = useState(false);
  const [trackingData, setTrackingData] = useState(null);
  const [dutySlots, setDutySlots] = useState({});
  const [timeModalOpen, setTimeModalOpen] = useState(false);
  const [timeModalDutyIndex, setTimeModalDutyIndex] = useState(null);
  const [timeModalDutyKey, setTimeModalDutyKey] = useState(null);
  const [timeModalSelectedSlot, setTimeModalSelectedSlot] = useState("");
  const [manualStartDate, setManualStartDate] = useState("");
  const [manualEndDate, setManualEndDate] = useState("");
  const [manualSlot, setManualSlot] = useState("");
  const [manualExcludeSaturday, setManualExcludeSaturday] = useState(false);
  const [manualExcludeSunday, setManualExcludeSunday] = useState(false);
  const [manualSelectedWeekDays, setManualSelectedWeekDays] = useState([]);
  const [showCalendarPreview, setShowCalendarPreview] = useState(false);
  const [calendarPreviewMonthKey, setCalendarPreviewMonthKey] = useState("");
  const [lookupData, setLookupData] = useState([]);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState("");
  const [requestFilter, setRequestFilter] = useState("pending");

  const requestFilterOptions = [
    { label: "Pending", value: "pending" },
    { label: "Confirmed", value: "confirmed" },
    { label: "Completed", value: "completed" },
    { label: "Others", value: "others" },
  ];

  useEffect(() => {
    getVisit();
  }, []);

  const getVisit = async () => {
    setLoading(true);
    try {
      const result = await getAssignedStaffList();
      setVisitList(result?.data || []);
    } catch (error) {
      console.error("Error fetching assignment data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visitList.length > 0 && !selectedPatient) {
      const firstPatient = visitList[0];

      if (firstPatient.dutyInfo && firstPatient.dutyInfo.length > 0) {
        const updatedDutyInfo = firstPatient.dutyInfo.map((duty) => {
          if (duty.attendedBy && duty.attendedBy.userID) return duty;

          const assignedStaffId = firstPatient.specialistInfo?.userID?.$oid;
          const assignedStaffName = firstPatient.specialistInfo?.name;
          const assignedStaffRole =
            firstPatient.specialistInfo?.role?.toLowerCase() || "nurse";

          return {
            ...duty,
            attendedBy: {
              userID: { $oid: assignedStaffId },
              role: assignedStaffRole,
            },
            assignedStaffName,
          };
        });

        setSelectedPatient({
          ...firstPatient,
          dutyInfo: updatedDutyInfo,
        });
      } else {
        setSelectedPatient(firstPatient);
      }
    }
  }, [visitList, selectedPatient]);

  useEffect(() => {
    if (!selectedPatient) {
      setManualStartDate("");
      setManualEndDate("");
      setManualSlot("");
      setManualExcludeSaturday(false);
      setManualExcludeSunday(false);
      setManualSelectedWeekDays([]);
      setShowCalendarPreview(false);
      setLookupError("");
      return;
    }

    const startDate = selectedPatient?.visitInfo?.startDate
      ? moment(selectedPatient.visitInfo.startDate).format("YYYY-MM-DD")
      : "";
    const endDate = selectedPatient?.visitInfo?.endDate
      ? moment(selectedPatient.visitInfo.endDate).format("YYYY-MM-DD")
      : "";

    setManualStartDate(startDate);
    setManualEndDate(endDate);
    setManualSlot(selectedPatient?.visitInfo?.slot || "");

    const activeAssessment = Array.isArray(selectedPatient?.assessment)
      ? selectedPatient.assessment.find((item) => item?.active)
      : null;

    const existingExcludedDays = Array.isArray(
      activeAssessment?.treatmentPlan?.excludedDays
    )
      ? activeAssessment.treatmentPlan.excludedDays
      : [];

    setManualExcludeSaturday(existingExcludedDays.includes("Saturday"));
    setManualExcludeSunday(existingExcludedDays.includes("Sunday"));

    const existingSessionDays = Array.isArray(
      activeAssessment?.treatmentPlan?.sessionDurationDays
    )
      ? activeAssessment.treatmentPlan.sessionDurationDays
      : [];

    const existingWeekDays = [
      ...new Set(
        existingSessionDays
          .map((item) => moment(item, "YYYY-MM-DD", true))
          .filter((item) => item.isValid())
          .map((item) => item.format("dddd"))
      ),
    ];

    setManualSelectedWeekDays(existingWeekDays);
    setShowCalendarPreview(false);
    setLookupError("");
  }, [selectedPatient?._id]);

  const getNames = async () => {
    if (!selectedPatient) return;

    const staff = selectedPatient?.specialistInfo?.role || null;
    const subRole = selectedPatient?.specialistInfo?.subRole ? selectedPatient?.specialistInfo?.subRole : "physiotherapist" || null;
    const visitStartDate = selectedPatient?.visitInfo?.startDate
      ? moment(selectedPatient.visitInfo.startDate).format("YYYY-MM-DD")
      : "";
    const selectedStartDate = manualStartDate || visitStartDate;

    if (!selectedStartDate) {
      if (needsManualVisitInfo) {
        if (!toast.isActive(START_DATE_REQUIRED_TOAST_ID)) {
          toast.warning("Please select start date before selecting staff", {
            toastId: START_DATE_REQUIRED_TOAST_ID,
          });
        }
      }
      return;
    }

    const date = new Date(selectedStartDate);

    console.log(staff, date, subRole)
    const result = await getStandByStaffs(staff, date, subRole);
    if (result?.data) {
      setAssignStaffList(result.data || []);
    }
  };

  const getStandbyStaffList = () => {
    const standbyList = [];
    const sps = selectedPatient?.standBySpecialists;

    if (sps?.specialists1UserID) {
      standbyList.push({
        id: String(sps.specialists1UserID),
        name: sps.specialists1Name,
      });
    }

    if (sps?.specialists2UserID) {
      standbyList.push({
        id: String(sps.specialists2UserID),
        name: sps.specialists2Name,
      });
    }

    if (selectedPatient?.standBySpecialist?.userID) {
      const id = String(selectedPatient.standBySpecialist.userID);
      if (!standbyList.some((s) => s.id === id)) {
        standbyList.push({
          id,
          name: selectedPatient.standBySpecialist.name,
        });
      }
    }

    if (selectedPatient?.backupSpecialist?.userID) {
      const id = String(selectedPatient.backupSpecialist.userID);
      if (!standbyList.some((s) => s.id === id)) {
        standbyList.push({
          id,
          name: selectedPatient.backupSpecialist.name,
        });
      }
    }

    return standbyList;
  };

  const handleRowClick = (patient) => {
    setSelectedMainStaff(null);
    setSelectedStandby1(null);
    setSelectedStandby2(null);
    setAssessmentStaff("");
    setIsEditingStaff(false);
    setEditingDutyIndex(null);
    setChangingDutyStaff({});
    setDutySlots({});
    setTimeModalOpen(false);
    setTimeModalDutyIndex(null);
    setTimeModalDutyKey(null);
    setTimeModalSelectedSlot("");
    setManualStartDate("");
    setManualEndDate("");
    setManualSlot("");
    setManualExcludeSaturday(false);
    setManualExcludeSunday(false);
    setManualSelectedWeekDays([]);
    setShowCalendarPreview(false);
    setLookupError("");

    if (selectedPatient?._id === patient._id) {
      setSelectedPatient(patient);
    } else {
      if (patient.dutyInfo && patient.dutyInfo.length > 0) {
        const updatedDutyInfo = patient.dutyInfo.map((duty) => {
          if (duty.attendedBy && duty.attendedBy.userID) return duty;

          const assignedStaffId = patient.specialistInfo?.userID?.$oid;
          const assignedStaffName = patient.specialistInfo?.name;
          const assignedStaffRole =
            patient.specialistInfo?.role?.toLowerCase() || "nurse";

          return {
            ...duty,
            attendedBy: {
              userID: { $oid: assignedStaffId },
              role: assignedStaffRole,
            },
            assignedStaffName,
          };
        });

        setSelectedPatient({
          ...patient,
          dutyInfo: updatedDutyInfo,
        });
      } else {
        setSelectedPatient(patient);
      }
    }
  };

  const handleSingleStaffChange = (staffId) => {
    setSelectedMainStaff(staffId);
    setIsEditingStaff(true);
  };

  const handleManualWeekDayToggle = (day) => {
    const dayName = String(day || "").trim();
    if (!dayName) return;

    setManualSelectedWeekDays((prev) => {
      const exists = prev.includes(dayName);
      if (exists) {
        return prev.filter((item) => item !== dayName);
      }
      if (requiredWeekDayCount > 0 && prev.length >= requiredWeekDayCount) {
        return prev;
      }
      return [...prev, dayName];
    });

    setIsEditingStaff(true);
  };

  const handleDutyStaffChange = async (dutyIndex, staffId) => {
    if (!staffId || !selectedPatient) return;

    const duty = selectedPatient.dutyInfo[dutyIndex];

    if (isDutyCompleted(duty)) {
      alert("Cannot change staff for a completed service.");
      return;
    }

    const selectedStaff = getStandbyStaffList().find(
      (staff) => staff.id === staffId
    );

    if (!selectedStaff) {
      alert("Selected staff not found");
      return;
    }

    const currentStaffName =
      duty?.attendedBy?.userID?.personalInfo?.basicInfo?.nameInfo?.title &&
        duty?.attendedBy?.userID?.personalInfo?.basicInfo?.nameInfo?.fullName
        ? `${duty.attendedBy.userID.personalInfo.basicInfo.nameInfo.title} ${duty.attendedBy.userID.personalInfo.basicInfo.nameInfo.fullName}`
        : "N/A";

    const confirmChange = window.confirm(
      `Are you sure you want to change staff for ${moment(duty.date).format(
        "DD/MM/YYYY"
      )}?\n\nFrom: ${currentStaffName}\nTo: ${selectedStaff.name}`
    );

    if (!confirmChange) {
      setEditingDutyIndex(null);
      const updatedChangingStaff = { ...changingDutyStaff };
      delete updatedChangingStaff[dutyIndex];
      setChangingDutyStaff(updatedChangingStaff);
      return;
    }

    const currentPatientId = selectedPatient?._id;

    const data = {
      visitID: currentPatientId,
      specialistID: selectedStaff.id,
      date: moment(duty.date).format("YYYY-MM-DD"),
    };

    try {
      await actingHCPChange(data);
      await getVisit();

      alert("Staff changed successfully!");
      setEditingDutyIndex(null);
      const updatedChangingStaff = { ...changingDutyStaff };
      delete updatedChangingStaff[dutyIndex];
      setChangingDutyStaff(updatedChangingStaff);
    } catch (error) {
      console.error("Error changing staff:", error);
      alert("Failed to change staff. Please try again.");

      setEditingDutyIndex(null);
      const updatedChangingStaff = { ...changingDutyStaff };
      delete updatedChangingStaff[dutyIndex];
      setChangingDutyStaff(updatedChangingStaff);
    }
  };

  const handleCloseDropdown = (dutyIndex) => {
    setEditingDutyIndex(null);
    const updatedChangingStaff = { ...changingDutyStaff };
    delete updatedChangingStaff[dutyIndex];
    setChangingDutyStaff(updatedChangingStaff);
  };

  const handleSaveStaffAssignment = async () => {
    if (!selectedPatient) {
      alert("No patient selected");
      return;
    }

    if (!selectedMainStaff || !selectedStandby1) {
      alert("Please select main staff and standby staff 1");
      return;
    }

    if (needsManualVisitInfo) {
      if (!effectiveManualStartDate) {
        alert("Please select start date");
        return;
      }

      if (!effectiveManualEndDate) {
        alert("Unable to calculate end date");
        return;
      }

      if (!effectiveManualSlot) {
        alert("Please select slot");
        return;
      }

      if (
        isPhysiotherapist &&
        isTwiceOrThriceFrequency &&
        manualSelectedWeekDays.length !== requiredWeekDayCount
      ) {
        alert(
          `Please select exactly ${requiredWeekDayCount} day${requiredWeekDayCount > 1 ? "s" : ""
          } for ${activeSessionFrequency || "selected frequency"}`
        );
        return;
      }

      if (
        isPhysiotherapist &&
        computedPhysioSessionDurationDays.length !== effectiveSessionCount
      ) {
        alert(
          `Unable to build ${effectiveSessionCount} sessions with selected frequency and days`
        );
        return;
      }
    }

    const safeStaffList = Array.isArray(assignStaffList) ? assignStaffList : [];

    const originalStaff = safeStaffList.find(
      (item) => item && item._id === selectedMainStaff
    );

    const standby1Staff = safeStaffList.find(
      (item) => item && item._id === selectedStandby1
    );

    const standby2Staff =
      !isPhysiotherapist && selectedStandby2
        ? safeStaffList.find((item) => item && item._id === selectedStandby2)
        : null;

    const effectiveAssessmentStaffId =
      isPhysiotherapist && !assessmentStaff ? selectedMainStaff : assessmentStaff;

    const assessmentStaffDoc = effectiveAssessmentStaffId
      ? safeStaffList.find(
        (item) => item && item._id === effectiveAssessmentStaffId
      )
      : null;

    if (!originalStaff) {
      alert("Selected main staff not found");
      return;
    }

    if (!standby1Staff) {
      alert("Selected standby staff 1 not found");
      return;
    }

    const currentPatientId = selectedPatient._id;

    const payload = {
      visitID: currentPatientId,
      dutyStaff: {
        userID: originalStaff._id,
        name: getStaffDisplayName(originalStaff),
      },
      standBySpecialist: {
        userID: standby1Staff._id,
        name: getStaffDisplayName(standby1Staff),
      },
      assessmentStaff: assessmentStaffDoc
        ? {
          userID: assessmentStaffDoc._id,
          name: getStaffDisplayName(assessmentStaffDoc),
        }
        : null,
    };

    if (!isPhysiotherapist && standby2Staff) {
      payload.backupSpecialist = {
        userID: standby2Staff._id,
        name: getStaffDisplayName(standby2Staff),
      };
    }

    if (needsManualVisitInfo) {
      const normalizedSlot = toTimeRangeLabel(effectiveManualSlot);
      payload.startDate = moment(
        effectiveManualStartDate,
        "YYYY-MM-DD",
        true
      ).utc().format();
      payload.endDate = moment(
        effectiveManualEndDate,
        "YYYY-MM-DD",
        true
      ).utc().format();
      payload.slot = normalizedSlot;

      if (isPhysiotherapist) {
        const sessionDurationDays = [...computedPhysioSessionDurationDays];
        const sessionStartDate =
          sessionDurationDays[0] || effectiveManualStartDate;
        const sessionEndDate =
          sessionDurationDays[sessionDurationDays.length - 1] ||
          effectiveManualEndDate;
        const requestedDate = moment().format("YYYY-MM-DD");
        const { startTimeStr, endTimeStr } = parseSelectedSlot(normalizedSlot);

        const sessionStartMoment = moment(
          `${sessionStartDate} ${startTimeStr}`,
          "YYYY-MM-DD hh:mm A",
          true
        );

        const sessionEndMoment = moment(
          `${sessionStartDate} ${endTimeStr}`,
          "YYYY-MM-DD hh:mm A",
          true
        );

        payload.treatmentPlan = {
          status: "pending",
          requestedDate,
          sessionDurationDays,
          sessionStartDate,
          sessionEndDate,
          excludedDays: physioExcludedDays,
          ...(sessionStartMoment.isValid()
            ? { sessionStartTime: sessionStartMoment.utc().format() }
            : {}),
          ...(sessionEndMoment.isValid()
            ? { sessionEndTime: sessionEndMoment.utc().format() }
            : {}),
          ...(startTimeStr && endTimeStr
            ? {
              timings: {
                startTime: sessionStartMoment.isValid()
                  ? sessionStartMoment.format()
                  : "",
                endTime: sessionEndMoment.isValid()
                  ? sessionEndMoment.format()
                  : "",
                label: normalizedSlot,
              },
            }
            : {}),
        };
      }
    }

    try {
      const res = await assignStaffs(payload);

      if (res?.error) {
        alert(res?.message || "Failed to assign staff");
        setSelectedMainStaff(null);
        setSelectedStandby1(null);
        setSelectedStandby2(null);
        setAssessmentStaff("");
        setIsEditingStaff(false);
        return;
      }

      const result = await getAssignedStaffList();

      if (result?.data) {
        const list = result.data || [];
        setVisitList(list);

        const refreshedPatient =
          currentPatientId && list.find((p) => p && p._id === currentPatientId);

        if (refreshedPatient) {
          setSelectedPatient(refreshedPatient);
        } else {
          setSelectedPatient(null);
        }
      }

      setSelectedMainStaff(null);
      setSelectedStandby1(null);
      setSelectedStandby2(null);
      setAssessmentStaff("");
      setIsEditingStaff(false);

      alert("Staff assigned successfully!");
    } catch (error) {
      console.error("Error assigning staff:", error);
      alert("Failed to assign staff");
    }
  };

  const handleCancelStaffAssignment = () => {
    setSelectedMainStaff(null);
    setSelectedStandby1(null);
    setSelectedStandby2(null);
    setAssessmentStaff("");
    setIsEditingStaff(false);

    setManualStartDate(
      selectedPatient?.visitInfo?.startDate
        ? moment(selectedPatient.visitInfo.startDate).format("YYYY-MM-DD")
        : ""
    );

    setManualEndDate(
      selectedPatient?.visitInfo?.endDate
        ? moment(selectedPatient.visitInfo.endDate).format("YYYY-MM-DD")
        : ""
    );

    setManualSlot(selectedPatient?.visitInfo?.slot || "");

    const activeAssessment = Array.isArray(selectedPatient?.assessment)
      ? selectedPatient.assessment.find((item) => item?.active)
      : null;

    const existingExcludedDays = Array.isArray(
      activeAssessment?.treatmentPlan?.excludedDays
    )
      ? activeAssessment.treatmentPlan.excludedDays
      : [];

    setManualExcludeSaturday(existingExcludedDays.includes("Saturday"));
    setManualExcludeSunday(existingExcludedDays.includes("Sunday"));

    const existingSessionDays = Array.isArray(
      activeAssessment?.treatmentPlan?.sessionDurationDays
    )
      ? activeAssessment.treatmentPlan.sessionDurationDays
      : [];

    const existingWeekDays = [
      ...new Set(
        existingSessionDays
          .map((item) => moment(item, "YYYY-MM-DD", true))
          .filter((item) => item.isValid())
          .map((item) => item.format("dddd"))
      ),
    ];

    setManualSelectedWeekDays(existingWeekDays);
    setShowCalendarPreview(false);
    setLookupError("");
  };

  const handleDeleteVisit = async (visitId) => {
    const confirmDelete = window.confirm(
      "Are you sure want to cancel this booking?"
    );
    if (!confirmDelete) return;

    try {
      console.log("Deleting visit with ID:", visitId);
      const res = await deleteVisit({ visitID: visitId });
      console.log("Delete visit response:", res);
      if (res?.error === false) {
        toast.success("Booking cancelled successfully");
        if (selectedPatient?._id === visitId) {
          setSelectedPatient(null);
        }
        await getVisit();
      } else {
        toast.error(res?.message || "Failed to cancel booking");
      }
    } catch (error) {
      console.error("Error deleting visit:", error);
      toast.error("Failed to cancel booking");
    }
  };

  const calculateAge = (dob) => {
    if (!dob) return "-";
    const age = moment().diff(moment(dob), "years");
    return `${age} yrs`;
  };

  const isPendingStatus = (patient) =>
    patient?.status?.toLowerCase() === "pending";

  const isRequestedStatus = (patient) =>
    patient?.status?.toLowerCase() === "payment pending";

  const isAssignedPatient = (patient) => patient?.assigned === true;

  const isDutyRequest = (patient) => {
    const status = String(patient?.status || "").toLowerCase();
    return ["pending", "payment pending", "not paid", "rejected"].includes(status);
  };

  const isVisitItem = (patient) => !isDutyRequest(patient);

  const getAssignedHCPName = (visit) => {
    if (!visit) return "N/A";
    return visit?.dutyStaff?.name || visit?.specialistInfo?.name || "N/A";
  };

  const getAssessmentHCPName = (visit) => {
    if (!visit) return "N/A";
    return visit?.assessmentStaff?.name || visit?.specialistInfo?.name || "N/A";
  };

  const getStandbyHCPName = (visit) => {
    if (!visit) return "N/A";
    return (
      visit?.standBySpecialist?.name ||
      visit?.standBySpecialists?.specialists1Name ||
      "N/A"
    );
  };

  const normalizeStatus = (v) =>
    String(v || "")
      .trim()
      .toLowerCase()
      .replace(/[\s_-]+/g, "");

  const getStatusKey = (item) => normalizeStatus(item?.status);

  const isPendingBucket = (item) => {
    const s = getStatusKey(item);
    return s === "pending" || s === "paymentpending" || s === "notpaid";
  };

  const isConfirmedBucket = (item) => {
    const s = getStatusKey(item);
    return s === "accepted" || s === "startduty";
  };

  const isCompletedBucket = (item) => {
    const s = getStatusKey(item);
    return s === "completed" || s === "endduty";
  };

  const isOthersBucket = (item) => {
    const s = getStatusKey(item);
    return s === "expired" || s === "rejected" || s === "skipped";
  };

  const bucketPredicate = (bucket) => {
    switch (bucket) {
      case "pending":
        return isPendingBucket;
      case "confirmed":
        return isConfirmedBucket;
      case "completed":
        return isCompletedBucket;
      case "others":
        return isOthersBucket;
      default:
        return () => true;
    }
  };



  const getServiceDateDisplay = (visitInfo) => {
    if (!visitInfo) return "-";
    const { startDate, endDate } = visitInfo;

    if (startDate && endDate) {
      const startStr = moment(startDate).format("DD/MM/YYYY");
      const endStr = moment(endDate).format("DD/MM/YYYY");
      if (startStr === endStr) return startStr;
      return `${startStr} - ${endStr}`;
    }

    if (startDate) return moment(startDate).format("DD/MM/YYYY");
    if (endDate) return moment(endDate).format("DD/MM/YYYY");
    return "-";
  };

  const filteredData = useMemo(() => {
    let filtered = Array.isArray(visitList) ? [...visitList] : [];

    filtered = filtered.filter(bucketPredicate(requestFilter));

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((item) => {
        const name = item?.patient?.name?.toLowerCase() || "";
        const phone = String(item?.patient?.phone ?? "");
        return name.includes(term) || phone.includes(term);
      });
    }

    return filtered;
  }, [visitList, searchTerm, requestFilter]);

  useEffect(() => {
    if (!filteredData.length) {
      setSelectedPatient(null);
      setDutySlots({});
      setTimeModalOpen(false);
      setTimeModalDutyIndex(null);
      setTimeModalDutyKey(null);
      setTimeModalSelectedSlot("");
      return;
    }

    if (
      selectedPatient &&
      filteredData.some((p) => p && p._id === selectedPatient._id)
    ) {
      return;
    }

    const first = filteredData[0];
    setDutySlots({});
    setTimeModalOpen(false);
    setTimeModalDutyIndex(null);
    setTimeModalDutyKey(null);
    setTimeModalSelectedSlot("");

    if (first.dutyInfo && first.dutyInfo.length > 0) {
      const updatedDutyInfo = first.dutyInfo.map((duty) => {
        if (duty.attendedBy && duty.attendedBy.userID) return duty;

        const assignedStaffId = first.specialistInfo?.userID?.$oid;
        const assignedStaffName = first.specialistInfo?.name;
        const assignedStaffRole =
          first.specialistInfo?.role?.toLowerCase() || "nurse";

        return {
          ...duty,
          attendedBy: {
            userID: { $oid: assignedStaffId },
            role: assignedStaffRole,
          },
          assignedStaffName,
        };
      });

      setSelectedPatient({
        ...first,
        dutyInfo: updatedDutyInfo,
      });
    } else {
      setSelectedPatient(first);
    }
  }, [filteredData, selectedPatient]);

  const isPhysiotherapist =
    selectedPatient?.specialistInfo?.role === "physiotherapist";

  const hasNoDutyInfo =
    !selectedPatient?.dutyInfo || selectedPatient?.dutyInfo.length === 0;

  const isPendingDutyRequest =
    !!selectedPatient && hasNoDutyInfo && isPendingStatus(selectedPatient);

  const isOneTimeDuty =
    String(selectedPatient?.dutyType || "").toLowerCase() === "onetime";

  const isRegularDuty =
    String(selectedPatient?.dutyType || "").toLowerCase() === "regular";

  const isPendingVisitStatus = isPendingStatus(selectedPatient);

  const hasMissingVisitInfo =
    !selectedPatient?.visitInfo?.startDate ||
    !selectedPatient?.visitInfo?.endDate ||
    !selectedPatient?.visitInfo?.slot;

  const activeAssessment = Array.isArray(selectedPatient?.assessment)
    ? selectedPatient.assessment.find((item) => item?.active)
    : null;

  const activeSessionFrequency = String(
    activeAssessment?.treatmentPlan?.sessionFrequency || ""
  ).trim();

  const normalizedSessionFrequency =
    normalizeSessionFrequency(activeSessionFrequency);

  const isDailyOrAlternativeFrequency =
    normalizedSessionFrequency === "daily" ||
    normalizedSessionFrequency === "alternative";

  const isTwiceOrThriceFrequency =
    normalizedSessionFrequency === "twice" ||
    normalizedSessionFrequency === "thrice";

  const requiredWeekDayCount =
    normalizedSessionFrequency === "twice"
      ? 2
      : normalizedSessionFrequency === "thrice"
        ? 3
        : 0;

  const needsManualVisitInfo =
    isPendingDutyRequest &&
    isPendingVisitStatus &&
    (isPhysiotherapist || isOneTimeDuty || isRegularDuty) &&
    hasMissingVisitInfo;

  const showInlineManualVisitInputs = needsManualVisitInfo;

  const existingStartDate = selectedPatient?.visitInfo?.startDate
    ? moment(selectedPatient.visitInfo.startDate).format("YYYY-MM-DD")
    : "";

  const effectiveManualStartDate = manualStartDate || existingStartDate;
  const effectiveManualSlot =
    manualSlot || selectedPatient?.visitInfo?.slot || "";

  const rawSessionCount = Number(selectedPatient?.visitInfo?.days);
  const effectiveSessionCount =
    Number.isFinite(rawSessionCount) && rawSessionCount > 0
      ? Math.floor(rawSessionCount)
      : 0;

  const physioExcludedDays = useMemo(() => {
    if (!isPhysiotherapist) return [];

    if (isTwiceOrThriceFrequency) {
      return WEEK_DAYS.filter((day) => !manualSelectedWeekDays.includes(day));
    }

    return [
      ...(manualExcludeSaturday ? ["Saturday"] : []),
      ...(manualExcludeSunday ? ["Sunday"] : []),
    ];
  }, [
    isPhysiotherapist,
    isTwiceOrThriceFrequency,
    manualSelectedWeekDays,
    manualExcludeSaturday,
    manualExcludeSunday,
  ]);

  const canComputePhysioSessions = useMemo(() => {
    if (
      !showInlineManualVisitInputs ||
      !isPhysiotherapist ||
      !effectiveManualStartDate ||
      effectiveSessionCount <= 0 ||
      !normalizedSessionFrequency
    ) {
      return false;
    }

    if (isTwiceOrThriceFrequency) {
      return manualSelectedWeekDays.length === requiredWeekDayCount;
    }

    return true;
  }, [
    showInlineManualVisitInputs,
    isPhysiotherapist,
    effectiveManualStartDate,
    effectiveSessionCount,
    normalizedSessionFrequency,
    isTwiceOrThriceFrequency,
    manualSelectedWeekDays.length,
    requiredWeekDayCount,
  ]);

  const computedPhysioSessionDurationDays = useMemo(() => {
    if (!canComputePhysioSessions) return [];

    return buildSessionDurationDays({
      startDate: effectiveManualStartDate,
      sessionCount: effectiveSessionCount,
      frequency: normalizedSessionFrequency,
      excludedDays: physioExcludedDays,
      selectedWeekDays: manualSelectedWeekDays,
    });
  }, [
    canComputePhysioSessions,
    effectiveManualStartDate,
    effectiveSessionCount,
    normalizedSessionFrequency,
    physioExcludedDays,
    manualSelectedWeekDays,
  ]);

  const computedPhysioEndDate = useMemo(() => {
    if (!computedPhysioSessionDurationDays.length) return "";
    return computedPhysioSessionDurationDays[
      computedPhysioSessionDurationDays.length - 1
    ];
  }, [computedPhysioSessionDurationDays]);

  const effectiveManualEndDate =
    isPhysiotherapist && needsManualVisitInfo
      ? computedPhysioEndDate
      : manualEndDate;

  const oneTimeManualSlotOptions = useMemo(() => {
    const options = [...ONE_TIME_MANUAL_SLOTS];
    if (selectedPatient?.visitInfo?.slot) {
      options.push(selectedPatient.visitInfo.slot);
    }
    return [
      ...new Set(
        options.map((item) => String(item || "").trim()).filter(Boolean)
      ),
    ];
  }, [selectedPatient?.visitInfo?.slot]);

  const physioManualSlotOptions = useMemo(() => {
    const options = [...PHYSIO_MANUAL_ASSIGN_SLOTS];
    if (selectedPatient?.visitInfo?.slot) {
      options.push(selectedPatient.visitInfo.slot);
    }
    return [
      ...new Set(
        options.map((item) => String(item || "").trim()).filter(Boolean)
      ),
    ];
  }, [selectedPatient?.visitInfo?.slot]);

  const regularLookupType = useMemo(
    () => getRegularLookupTypeByShift(selectedPatient?.visitInfo?.shiftType),
    [selectedPatient?.visitInfo?.shiftType]
  );

  const isTwentyFourBySevenRegularShift = useMemo(
    () => isTwentyFourBySevenShiftType(selectedPatient?.visitInfo?.shiftType),
    [selectedPatient?.visitInfo?.shiftType]
  );

  const regularManualSlotOptions = useMemo(() => {
    const options = [];

    if (regularLookupType) {
      lookupData
        .filter((item) => item?.type === regularLookupType)
        .forEach((item) => {
          options.push(...extractLookupSlotValues(item));
        });
    }

    if (selectedPatient?.visitInfo?.slot) {
      options.push(selectedPatient.visitInfo.slot);
    }

    return [
      ...new Set(
        options.map((item) => String(item || "").trim()).filter(Boolean)
      ),
    ];
  }, [lookupData, regularLookupType, selectedPatient?.visitInfo?.slot]);

  const manualTimeSlotOptions = useMemo(() => {
    if (isPhysiotherapist) return physioManualSlotOptions;
    if (isRegularDuty && isTwentyFourBySevenRegularShift) {
      return oneTimeManualSlotOptions;
    }
    return isRegularDuty ? regularManualSlotOptions : oneTimeManualSlotOptions;
  }, [
    isPhysiotherapist,
    isRegularDuty,
    isTwentyFourBySevenRegularShift,
    physioManualSlotOptions,
    regularManualSlotOptions,
    oneTimeManualSlotOptions,
  ]);

  const dischargeSummariesForHistory = useMemo(() => {
    const summaryDoc = selectedPatient?.visitInfo?.dischargeSummaryDoc;
    if (!summaryDoc) return [];

    if (Array.isArray(summaryDoc)) {
      return summaryDoc.filter((item) => item && typeof item === "object");
    }

    return typeof summaryDoc === "object" ? [summaryDoc] : [];
  }, [selectedPatient?.visitInfo?.dischargeSummaryDoc]);

  useEffect(() => {
    if (!showInlineManualVisitInputs || !isRegularDuty || isPhysiotherapist)
      return;

    if (isTwentyFourBySevenRegularShift) {
      setLookupError("");
      setLookupLoading(false);
      return;
    }

    if (!regularLookupType) {
      setLookupError("No slot mapping available for this shift type");
      return;
    }

    setLookupError("");

    if (lookupData.length > 0) return;

    let active = true;

    const loadLookup = async () => {
      try {
        setLookupLoading(true);
        setLookupError("");
        const result = await getLookupData();

        if (!active) return;

        if (result?.error === false && Array.isArray(result?.data)) {
          setLookupData(result.data);
        } else {
          setLookupData([]);
          setLookupError(result?.message || "Failed to load lookup slots");
        }
      } catch (error) {
        if (!active) return;
        setLookupData([]);
        setLookupError("Failed to load lookup slots");
      } finally {
        if (active) setLookupLoading(false);
      }
    };

    loadLookup();

    return () => {
      active = false;
    };
  }, [
    showInlineManualVisitInputs,
    isRegularDuty,
    isPhysiotherapist,
    isTwentyFourBySevenRegularShift,
    regularLookupType,
    lookupData.length,
  ]);

  useEffect(() => {
    if (!needsManualVisitInfo) return;

    if (isPhysiotherapist) {
      setManualEndDate(computedPhysioEndDate || "");
      return;
    }

    const computedEndDate = computeEndDateFromStartAndDays(
      effectiveManualStartDate,
      selectedPatient?.visitInfo?.days
    );

    setManualEndDate(computedEndDate);
  }, [
    needsManualVisitInfo,
    isPhysiotherapist,
    computedPhysioEndDate,
    effectiveManualStartDate,
    selectedPatient?.visitInfo?.days,
  ]);

  useEffect(() => {
    if (!isRegularDuty) {
      setLookupError("");
      setLookupLoading(false);
    }
  }, [isRegularDuty]);

  const isManualVisitInfoValid =
    !needsManualVisitInfo ||
    (Boolean(effectiveManualStartDate) &&
      Boolean(effectiveManualEndDate) &&
      Boolean(effectiveManualSlot) &&
      (!isPhysiotherapist ||
        computedPhysioSessionDurationDays.length === effectiveSessionCount) &&
      (!isPhysiotherapist ||
        !isTwiceOrThriceFrequency ||
        manualSelectedWeekDays.length === requiredWeekDayCount) &&
      (!isRegularDuty || !lookupLoading));

  const previewSessionDurationDays = useMemo(() => {
    if (!showInlineManualVisitInputs || !isPhysiotherapist) {
      return [];
    }

    return computedPhysioSessionDurationDays;
  }, [
    showInlineManualVisitInputs,
    isPhysiotherapist,
    computedPhysioSessionDurationDays,
  ]);

  const previewSessionDaySet = useMemo(
    () => new Set(previewSessionDurationDays),
    [previewSessionDurationDays]
  );

  const calendarPreviewMonths = useMemo(() => {
    if (
      !showInlineManualVisitInputs ||
      !isPhysiotherapist ||
      previewSessionDurationDays.length === 0
    ) {
      return [];
    }

    const start = moment(
      previewSessionDurationDays[0],
      "YYYY-MM-DD",
      true
    ).startOf("month");

    const end = moment(
      previewSessionDurationDays[previewSessionDurationDays.length - 1],
      "YYYY-MM-DD",
      true
    ).startOf("month");

    if (!start.isValid() || !end.isValid() || start.isAfter(end, "month")) {
      return [];
    }

    const months = [];
    const cursor = start.clone();
    let safetyCounter = 0;

    while (cursor.isSameOrBefore(end, "month") && safetyCounter < 12) {
      months.push({
        key: cursor.format("YYYY-MM"),
        label: cursor.format("MMMM YYYY"),
        cells: buildMonthCalendarCells(cursor),
      });
      cursor.add(1, "month");
      safetyCounter += 1;
    }

    return months;
  }, [
    showInlineManualVisitInputs,
    isPhysiotherapist,
    previewSessionDurationDays,
  ]);

  useEffect(() => {
    if (!showCalendarPreview || calendarPreviewMonths.length === 0) {
      setCalendarPreviewMonthKey("");
      return;
    }

    const exists = calendarPreviewMonths.some(
      (month) => month.key === calendarPreviewMonthKey
    );

    if (!exists) {
      setCalendarPreviewMonthKey(calendarPreviewMonths[0].key);
    }
  }, [showCalendarPreview, calendarPreviewMonths, calendarPreviewMonthKey]);

  const activeCalendarMonth = useMemo(() => {
    if (!calendarPreviewMonths.length) return null;
    return (
      calendarPreviewMonths.find(
        (month) => month.key === calendarPreviewMonthKey
      ) || calendarPreviewMonths[0]
    );
  }, [calendarPreviewMonths, calendarPreviewMonthKey]);

  const activeCalendarMonthIndex = useMemo(() => {
    if (!activeCalendarMonth) return -1;
    return calendarPreviewMonths.findIndex(
      (month) => month.key === activeCalendarMonth.key
    );
  }, [calendarPreviewMonths, activeCalendarMonth]);

  const canGoPrevCalendarMonth = activeCalendarMonthIndex > 0;
  const canGoNextCalendarMonth =
    activeCalendarMonthIndex >= 0 &&
    activeCalendarMonthIndex < calendarPreviewMonths.length - 1;

  const getColumns = () => {
    const cols = [
      {
        Header: () => (
          <div className="font-poppins ml-4 whitespace-nowrap">Name</div>
        ),
        accessor: "patient.name",
        Cell: ({ row: { original } }) => (
          <div className="font-poppins flex items-center gap-2">
            <div
              className={`w-2 h-2 rounded-full ${selectedPatient?._id === original._id
                ? "bg-blue-600"
                : "bg-transparent"
                }`}
            />
            <p className="text-blue-600 truncate max-w-[140px]">
              {original?.patient?.name || "N/A"}
            </p>
          </div>
        ),
      },
      {
        Header: () => (
          <div className="font-poppins whitespace-nowrap">Gender</div>
        ),
        accessor: "patient.gender",
        Cell: ({ row: { original } }) => (
          <div className="font-poppins">
            {original?.patient?.gender
              ? capitalizeFirstLetter(original.patient.gender)
              : "-"}
          </div>
        ),
      },
      {
        Header: () => <div className="font-poppins whitespace-nowrap">Age</div>,
        accessor: "patient.dob",
        Cell: ({ row: { original } }) => (
          <div className="font-poppins">
            {calculateAge(
              original?.patientInfo?.personalInfo?.basicInfo?.otherInfo?.dob ||
              null
            )}
          </div>
        ),
      },
      {
        Header: () => (
          <div className="flex justify-start items-center text-[13px] font-poppins text-left whitespace-nowrap">
            Service
          </div>
        ),
        accessor: "specialistInfo.role",
        Cell: ({ row: { original } }) => (
          <div className="flex justify-start items-center text-left text-[12px] max-w-[150px] font-poppins truncate">
            {original?.specialistInfo?.role
              ? capitalizeFirstLetter(original.specialistInfo.role)
              : "-"}
          </div>
        ),
      },
      // {
      //   Header: () => (
      //     <div className="font-poppins whitespace-nowrap">Day</div>
      //   ),
      //   accessor: "serviceDay",
      //   Cell: ({ row: { original } }) => (
      //     <div className="font-poppins text-[12px] whitespace-nowrap">
      //       {getCurrentServiceDayLabel(original)}
      //     </div>
      //   ),
      //   disableSortBy: true,
      // },
      {
        Header: () => (
          <div className="flex justify-start items-center text-[13px] font-poppins text-left whitespace-nowrap">
            Location
          </div>
        ),
        accessor: "visitInfo.addressInfo.city",
        Cell: ({ row: { original } }) => (
          <div className="flex justify-start items-center text-center text-[12px] font-poppins">
            {getLocationDisplay(original)}
          </div>
        ),
      },
      {
        Header: () => (
          <div className="flex justify-start items-center text-[13px] font-poppins text-left whitespace-nowrap">
            Duty Type
          </div>
        ),
        accessor: "visitInfo.shiftType",
        Cell: ({ row: { original } }) => (
          <div className="flex justify-start items-center text-left text-[12px] max-w-[170px] font-poppins truncate">
            {getDutyTypeDisplay(original)}
          </div>
        ),
      },
      {
        Header: () => (
          <div className="flex justify-start items-center text-[13px] font-poppins text-left whitespace-nowrap">
            Days
          </div>
        ),
        accessor: "visitInfo.days",
        Cell: ({ row: { original } }) => {
          const days = Number(original?.visitInfo?.days);
          return (
            <div className="flex justify-start items-center text-left text-[12px] font-poppins whitespace-nowrap">
              {Number.isFinite(days) ? days : "-"}
            </div>
          );
        },
      },
      {
        Header: () => (
          <div className="font-poppins whitespace-nowrap ml-2">Status</div>
        ),
        accessor: "status",
        Cell: ({ row: { original } }) => {
          const dutyStatus = getDutyStatus(original);
          return (
            <div className="flex justify-start text-center">
              <p>
                <StatusBadge status={dutyStatus} />
              </p>
            </div>
          );
        },
      },
      {
        Header: () => (
          <div className="font-poppins whitespace-nowrap text-center">Action</div>
        ),
        accessor: "deleteAction",
        Cell: ({ row: { original } }) => {
          const isPending = String(original?.status || "")
            .toLowerCase()
            .trim() === "pending";

          if (!isPending) return <div className="flex justify-center">-</div>;

          return (
            <div className="flex justify-center">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteVisit(original._id);
                }}
                className="p-1.5 rounded-full hover:bg-red-50 text-red-500 transition-colors"
                title="Cancel Booking"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </button>
            </div>
          );
        },
        disableSortBy: true,
      },

    ];

    return cols;
  };

  const columns = useMemo(() => getColumns(), [selectedPatient]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page: tablePage,
    prepareRow,
    gotoPage,
  } = useTable(
    {
      columns,
      data: filteredData,
      initialState: { pageSize: 12, pageIndex: paginationPage },
    },
    useSortBy,
    usePagination
  );

  console.log(filteredData)

  useEffect(() => {
    gotoPage(paginationPage);
  }, [paginationPage, gotoPage, filteredData]);

  if (loading && visitList.length === 0) {
    return (
      <div>
        <Loader loading />
      </div>
    );
  }

  if (!loading && visitList.length === 0) {
    return (
      <div className="flex flex-col w-full h-[90vh] gap-3 p-2">
        <div className="bg-white rounded-2xl shadow-md border border-[#E3E7F0] flex flex-col justify-center items-center w-[100%] h-[85vh]">
          <div className="w-14 h-14 rounded-full bg-[#EEF2FF] flex items-center justify-center mb-4">
            <img
              src={CommonImages.noDuty}
              className="h-[125px] w-[160px] object-contain"
              alt="No Duty"
            />
          </div>
          <h2 className="text-[14px] font-semibold font-poppins text-[#787998] mb-1">
            No visits available
          </h2>
        </div>
      </div>
    );
  }

  const openTimeModal = (dutyIndex, dutyKey, currentSlot) => {
    if (!isPhysiotherapist) return;

    const alertMsg =
      "Alert: Booking Slot Change Notice\n\n" +
      "You are attempting to change the scheduled booking slot for an assigned visit.\n" +
      "Please ensure that any changes are made only after clear communication and mutual agreement between you and the patient.\n\n" +
      'Kindly note that this process is solely between you and the patient. We ("Ceviliyar") do not participate in, monitor, or assume responsibility for any booking slot changes made.';

    const proceed = window.confirm(alertMsg);
    if (!proceed) return;

    setTimeModalDutyIndex(dutyIndex);
    setTimeModalDutyKey(dutyKey);
    setTimeModalSelectedSlot(
      currentSlot || selectedPatient?.visitInfo?.slot || PHYSIO_TIME_SLOTS[0]
    );
    setTimeModalOpen(true);
  };

  const closeTimeModal = () => {
    setTimeModalOpen(false);
    setTimeModalDutyIndex(null);
    setTimeModalDutyKey(null);
    setTimeModalSelectedSlot("");
  };

  const applySelectedTimeSlot = async () => {
    if (
      !selectedPatient ||
      timeModalDutyIndex == null ||
      !timeModalSelectedSlot
    ) {
      return;
    }

    const dutyForModal = selectedPatient.dutyInfo?.[timeModalDutyIndex] || null;
    if (!dutyForModal) return;

    const dutyDateRaw = dutyForModal.date;
    const dateStr = moment(dutyDateRaw).format("YYYY-MM-DD");
    const { startTimeStr, endTimeStr } = parseSelectedSlot(timeModalSelectedSlot);

    if (!startTimeStr || !endTimeStr) {
      toast.error("Invalid time slot selected");
      return;
    }

    const startMoment = moment(
      `${dateStr} ${startTimeStr}`,
      "YYYY-MM-DD hh:mm A"
    );
    const endMoment = moment(
      `${dateStr} ${endTimeStr}`,
      "YYYY-MM-DD hh:mm A"
    );

    const startTime = startMoment.format("YYYY-MM-DDTHH:mm:ss.SSSZ");
    const endTime = endMoment.format("YYYY-MM-DDTHH:mm:ss.SSSZ");

    const payload = {
      visitID: selectedPatient._id,
      startTime: moment(startTime).utc().format(),
      endTime: moment(endTime).utc().format(),
      date: dateStr,
    };

    try {
      const response = await timeSlotChange(payload);

      if (response?.error !== true) {
        toast.success("Time slot updated successfully");

        if (timeModalDutyKey) {
          setDutySlots((prev) => ({
            ...prev,
            [timeModalDutyKey]: timeModalSelectedSlot,
          }));
        }

        closeTimeModal();
      } else {
        toast.error("Failed to update time slot");
      }
    } catch (err) {
      console.error("Error updating time slot:", err);
      toast.error("Something went wrong while updating time slot");
    }
  };

  const dutyForModal =
    timeModalDutyIndex != null
      ? selectedPatient?.dutyInfo?.[timeModalDutyIndex]
      : null;

  const isPhysioNoAssessment =
    isPhysiotherapist && selectedPatient?.isAssessment === false;

  const assessmentLabel = isPhysioNoAssessment ? "Duty Staff" : "Assessment Staff";
  const dutyLabel = isPhysioNoAssessment ? "Standby Staff 1" : "Duty Staff";
  const standby1Label = isPhysioNoAssessment ? "Standby Staff 2" : "Standby Staff 1";
  const physioStandbyLabel = isPhysioNoAssessment ? "Standby Staff 2" : "Standby Staff";
  const standby2Label = "Standby Staff 2";

  const tableStaffHeader =
    isPhysiotherapist &&
      (!selectedPatient?.dutyInfo || selectedPatient.dutyInfo.length === 0)
      ? assessmentLabel
      : "Assigned Staff";

  const minStartDate = moment().startOf("day").add(1, "day").format("YYYY-MM-DD");

  return (
    <div className="flex flex-col min-h-screen gap-3 overflow-x-auto">
      <div className="flex flex-row items-start justify-start w-full flex-1 min-h-0 gap-3 p-2">
        <div className="flex-1 flex flex-col gap-3 min-h-0">
          <div className="bg-white rounded-2xl shadow-lg p-2 h-[44vh]">
            <div className="flex justify-between items-center p-2 border-b border-gray-100">
              <p className="text-[15px] font-semibold font-poppins whitespace-nowrap">
                Patient List ({filteredData.length})
              </p>

              <select
                value={requestFilter}
                onChange={(e) => {
                  setPaginationPage(0);
                  setRequestFilter(e.target.value);
                }}
                className="border rounded-md text-[12px] text-blue-600 border-blue-600 py-1 px-3"
                style={{ borderWidth: 1.5 }}
              >
                {requestFilterOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="overflow-x-auto">
              <div className="w-full h-[35vh] overflow-y-auto">
                <table {...getTableProps()} className="w-full">
                  <thead className="bg-[#F6F8FC] sticky top-0 z-10">
                    {headerGroups.map((headerGroup) => (
                      <tr {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map((column) => (
                          <th
                            {...column.getHeaderProps(
                              column.getSortByToggleProps()
                            )}
                            className="px-3 py-2 text-left text-[12px] font-medium text-[#8177A8] tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                          >
                            <div className="flex items-center gap-1">
                              {column.render("Header")}
                              {column.canSort && (
                                <SortIcon
                                  isSorted={column.isSorted}
                                  isSortedDesc={column.isSortedDesc}
                                />
                              )}
                            </div>
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>

                  <tbody
                    {...getTableBodyProps()}
                    className="bg-white divide-y divide-gray-200"
                  >
                    {tablePage.map((row) => {
                      prepareRow(row);
                      const isSelected = selectedPatient?._id === row.original._id;

                      return (
                        <tr
                          {...row.getRowProps()}
                          onClick={() => handleRowClick(row.original)}
                          className={`cursor-pointer transition-colors ${isSelected
                            ? "bg-[#DFEAFF] border-l-4 border-blue-600"
                            : "hover:bg-gray-50"
                            }`}
                        >
                          {row.cells.map((cell) => (
                            <td
                              {...cell.getCellProps()}
                              className="px-3 py-1 text-[12px] text-gray-700 whitespace-nowrap"
                            >
                              {cell.render("Cell")}
                            </td>
                          ))}
                        </tr>
                      );
                    })}

                    {tablePage.length === 0 && (
                      <tr>
                        <td
                          colSpan={headerGroups[0]?.headers?.length || 1}
                          className="text-center text-[12px] text-gray-500 py-6 font-poppins"
                        >
                          No records available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-3 h-[43vh] flex flex-col">
            <h3 className="text-[15px] font-semibold font-poppins text-gray-800 mb-3 whitespace-nowrap">
              Assign staff
            </h3>

            {!selectedPatient ? (
              <div className="flex-1 flex items-center justify-center text-[12px] text-gray-500 font-poppins">
                No visit data available
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-x-auto overflow-y-auto">
                  <table className="min-w-full border border-gray-200 rounded-[12px]">
                    <thead className="bg-[#F6F8FC]">
                      <tr>
                        <th className="px-4 py-2 text-left text-[12px] font-semibold text-[#8177A8] border-b font-poppins whitespace-nowrap">
                          {showInlineManualVisitInputs ? "Start Date" : "Service Date"}
                        </th>

                        {showInlineManualVisitInputs && (
                          <th className="px-4 py-2 text-left text-[12px] font-semibold text-[#8177A8] border-b font-poppins whitespace-nowrap">
                            End Date
                          </th>
                        )}

                        <th className="px-4 py-2 text-left text-[12px] font-semibold text-[#8177A8] border-b font-poppins whitespace-nowrap">
                          Time
                        </th>

                        <th className="px-4 py-2 text-left text-[12px] font-semibold text-[#8177A8] border-b font-poppins whitespace-nowrap">
                          {tableStaffHeader}
                        </th>
                      </tr>
                    </thead>

                    <tbody className="bg-white divide-y divide-gray-200">
                      {selectedPatient.dutyInfo && selectedPatient.dutyInfo.length > 0 ? (
                        selectedPatient.dutyInfo.map((duty, index) => {
                          const standbyStaffList = getStandbyStaffList();
                          const totalDays = selectedPatient.dutyInfo.length || 0;
                          const currentDayNumber =
                            duty.day != null ? duty.day : index + 1;
                          const dutyCompleted = isDutyCompleted(duty);
                          const dutyKey = duty._id || index;
                          const displaySlot =
                            dutySlots[dutyKey] ||
                            duty.slot ||
                            selectedPatient?.visitInfo?.slot ||
                            "-";

                          return (
                            <tr key={duty._id || index} className="hover:bg-[#DFEAFF]">
                              <td className="px-4 py-1 text-[12px] text-gray-700 font-medium font-poppins whitespace-nowrap">
                                {moment(duty.date).format("DD/MM/YYYY - dddd")}
                                {totalDays > 0 && (
                                  <span className="ml-1 text-[11px] text-gray-500">
                                    ({currentDayNumber}/{totalDays})
                                  </span>
                                )}
                              </td>

                              <td className="px-4 py-1 text-[12px] text-gray-700 font-poppins whitespace-nowrap">
                                <div className="inline-flex items-center gap-2">
                                  <span className="text-[12px] font-medium text-gray-800">
                                    {displaySlot}
                                  </span>

                                  {isPhysiotherapist && !dutyCompleted && (
                                    <button
                                      onClick={() =>
                                        openTimeModal(index, dutyKey, displaySlot)
                                      }
                                      className="text-[11px] text-blue-600 px-2 py-[2px] font-poppins"
                                    >
                                      <img
                                        src={CommonImages.editIconBlue}
                                        className="text-[#4361EE] w-6 h-6"
                                        alt="Edit"
                                      />
                                    </button>
                                  )}
                                </div>
                              </td>

                              <td className="px-4 py-1 whitespace-nowrap">
                                <div className="flex items-center gap-2">
                                  {editingDutyIndex === index ? (
                                    <div className="relative flex-1 flex items-center gap-2">
                                      <select
                                        value={changingDutyStaff[index] || ""}
                                        onChange={(e) => {
                                          if (dutyCompleted) return;
                                          setChangingDutyStaff({
                                            ...changingDutyStaff,
                                            [index]: e.target.value,
                                          });
                                          handleDutyStaffChange(index, e.target.value);
                                        }}
                                        autoFocus
                                        disabled={dutyCompleted}
                                        className="w-full bg-[#F6F8FC] rounded-md px-2 py-1.5 pr-8 text-[12px] font-poppins appearance-none cursor-pointer focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 border border-gray-300 disabled:opacity-60 disabled:cursor-not-allowed"
                                      >
                                        <option value="" hidden>
                                          Select staff
                                        </option>
                                        {standbyStaffList.map((staff) => (
                                          <option key={staff.id} value={staff.id}>
                                            {staff.name}
                                          </option>
                                        ))}
                                      </select>

                                      {!dutyCompleted && (
                                        <>
                                          <div className="pointer-events-none absolute inset-y-0 right-10 flex items-center px-2 text-gray-700">
                                            <svg
                                              className="fill-current h-3 w-3"
                                              xmlns="http://www.w3.org/2000/svg"
                                              viewBox="0 0 20 20"
                                            >
                                              <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                                            </svg>
                                          </div>

                                          <button
                                            onClick={() => handleCloseDropdown(index)}
                                            className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-200 rounded-full transition-colors"
                                          >
                                            <svg
                                              className="w-4 h-4 text-gray-600"
                                              fill="none"
                                              stroke="currentColor"
                                              viewBox="0 0 24 24"
                                              xmlns="http://www.w3.org/2000/svg"
                                            >
                                              <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M6 18L18 6M6 6l12 12"
                                              />
                                            </svg>
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  ) : (
                                    <>
                                      <div className="flex items-center gap-2">
                                        <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center text-[14px]">
                                          <img
                                            src={CommonImages.nurseNew}
                                            alt="Avatar"
                                            className="w-7 h-7 object-cover rounded-full"
                                          />
                                        </div>

                                        <span className="text-[12px] text-[#4361EE] font-medium font-poppins truncate max-w-[160px]">
                                          {duty?.attendedBy?.userID?.personalInfo?.basicInfo?.nameInfo?.title &&
                                            duty?.attendedBy?.userID?.personalInfo?.basicInfo?.nameInfo?.fullName
                                            ? `${duty.attendedBy.userID.personalInfo.basicInfo.nameInfo.title} ${duty.attendedBy.userID.personalInfo.basicInfo.nameInfo.fullName}`
                                            : "N/A"}
                                        </span>
                                      </div>

                                      {standbyStaffList.length > 0 && !dutyCompleted && (
                                        <button
                                          onClick={() => setEditingDutyIndex(index)}
                                          className="p-1.5 hover:bg-blue-50 rounded-full transition-colors"
                                        >
                                          <img
                                            src={CommonImages.editIconBlue}
                                            className="text-[#4361EE] w-6 h-6"
                                            alt="Edit"
                                          />
                                        </button>
                                      )}
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr className="hover:bg-[#DFEAFF]">
                          <td className="px-4 py-1 text-[12px] text-gray-700 font-medium font-poppins whitespace-nowrap">
                            {showInlineManualVisitInputs ? (
                              <input
                                type="date"
                                value={effectiveManualStartDate || ""}
                                min={minStartDate}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  if (val && moment(val).isSameOrBefore(moment(), "day")) return;
                                  setManualStartDate(val);
                                  setIsEditingStaff(true);
                                }}
                                disabled={!isPendingStatus(selectedPatient)}
                                className="w-full bg-[#F6F8FC] rounded-md px-2 py-1.5 text-[12px] font-poppins border border-gray-300 outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                              />
                            ) : (
                              getServiceDateDisplay(selectedPatient?.visitInfo)
                            )}
                          </td>

                          {showInlineManualVisitInputs && (
                            <td className="px-4 py-1 text-[12px] text-gray-700 font-poppins whitespace-nowrap">
                              <input
                                type="date"
                                value={effectiveManualEndDate}
                                readOnly
                                disabled
                                className="w-full bg-gray-100 rounded-md px-2 py-1.5 text-[12px] font-poppins border border-gray-300 outline-none cursor-not-allowed"
                              />
                            </td>
                          )}

                          <td className="px-4 py-1 text-[12px] text-gray-700 font-poppins whitespace-nowrap">
                            {showInlineManualVisitInputs ? (
                              <div className="relative w-full">
                                <select
                                  value={effectiveManualSlot || ""}
                                  onChange={(e) => {
                                    setManualSlot(e.target.value);
                                    setIsEditingStaff(true);
                                  }}
                                  disabled={
                                    !isPendingStatus(selectedPatient) ||
                                    (isRegularDuty &&
                                      (lookupLoading ||
                                        manualTimeSlotOptions.length === 0))
                                  }
                                  className="w-full bg-[#F6F8FC] rounded-md px-2 py-1.5 text-[12px] font-poppins appearance-none cursor-pointer focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 border border-gray-300 disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                  <option value="" hidden>
                                    {isRegularDuty && lookupLoading
                                      ? "Loading slots..."
                                      : "Select slot"}
                                  </option>
                                  {manualTimeSlotOptions.map((slotValue) => (
                                    <option key={slotValue} value={slotValue}>
                                      {slotValue}
                                    </option>
                                  ))}
                                </select>

                                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                                  <svg
                                    className="fill-current h-3 w-3"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 20 20"
                                  >
                                    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                                  </svg>
                                </div>
                              </div>
                            ) : (
                              selectedPatient?.visitInfo?.slot || manualSlot || "-"
                            )}
                          </td>

                          <td className="px-4 py-1 whitespace-nowrap">
                            {isRequestedStatus(selectedPatient) ? (
                              <div className="flex items-center gap-2">
                                <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center text-[14px]">
                                  <img
                                    src={CommonImages.nurseNew}
                                    alt="Avatar"
                                    className="w-7 h-7 object-cover rounded-full"
                                  />
                                </div>
                                <span className="text-[12px] text-[#4361EE] font-medium font-poppins truncate max-w-[160px]">
                                  {isPhysiotherapist
                                    ? getAssessmentHCPName(selectedPatient)
                                    : getAssignedHCPName(selectedPatient)}
                                </span>
                              </div>
                            ) : (
                              <div className="relative w-full">
                                <select
                                  value={
                                    isPhysiotherapist
                                      ? assessmentStaff || ""
                                      : selectedMainStaff || ""
                                  }
                                  onChange={(e) => {
                                    if (isPhysiotherapist) {
                                      setAssessmentStaff(e.target.value);
                                      setSelectedMainStaff(e.target.value);
                                      setIsEditingStaff(true);
                                    } else {
                                      handleSingleStaffChange(e.target.value);
                                    }
                                  }}
                                  onFocus={async () => {
                                    await getNames();
                                  }}
                                  disabled={!isPendingStatus(selectedPatient)}
                                  className={`w-full bg-[#F6F8FC] rounded-md px-2 py-1.5 text-[12px] font-poppins appearance-none cursor-pointer focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 border border-gray-300 ${!isPendingStatus(selectedPatient)
                                    ? "opacity-50 cursor-not-allowed"
                                    : ""
                                    }`}
                                >
                                  <option value="" hidden>
                                    {isPhysiotherapist
                                      ? `Select ${assessmentLabel}`
                                      : "Select staff"}
                                  </option>
                                  {assignStaffList.map((staff) => (
                                    <option key={staff._id} value={staff._id}>
                                      {getStaffDisplayName(staff)}
                                    </option>
                                  ))}
                                </select>

                                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                                  <svg
                                    className="fill-current h-3 w-3"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 20 20"
                                  >
                                    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                  {showInlineManualVisitInputs &&
                    !isPhysiotherapist &&
                    isRegularDuty &&
                    (lookupError ||
                      (!lookupLoading &&
                        regularLookupType &&
                        manualTimeSlotOptions.length === 0)) && (
                      <p className="text-[11px] text-red-500 font-poppins mt-2">
                        {lookupError || "No slots available for selected shift type"}
                      </p>
                    )}

                  {showInlineManualVisitInputs &&
                    isPhysiotherapist &&
                    (isDailyOrAlternativeFrequency || isTwiceOrThriceFrequency) && (
                      <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-3">
                        <div className="border border-gray-200 rounded-[12px] p-3">
                          <p className="text-[12px] font-semibold text-[#1D1D22] font-poppins mb-2">
                            Session Frequency: {activeSessionFrequency || "-"}
                          </p>

                          {isDailyOrAlternativeFrequency && (
                            <div className="flex flex-wrap gap-4">
                              <label className="inline-flex items-center gap-2 text-[12px] text-gray-700 font-poppins">
                                <input
                                  type="checkbox"
                                  checked={manualExcludeSaturday}
                                  onChange={(e) => {
                                    setManualExcludeSaturday(e.target.checked);
                                    setIsEditingStaff(true);
                                  }}
                                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                  disabled={!isPendingStatus(selectedPatient)}
                                />
                                Exclude Saturday
                              </label>

                              <label className="inline-flex items-center gap-2 text-[12px] text-gray-700 font-poppins">
                                <input
                                  type="checkbox"
                                  checked={manualExcludeSunday}
                                  onChange={(e) => {
                                    setManualExcludeSunday(e.target.checked);
                                    setIsEditingStaff(true);
                                  }}
                                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                  disabled={!isPendingStatus(selectedPatient)}
                                />
                                Exclude Sunday
                              </label>
                            </div>
                          )}

                          {isTwiceOrThriceFrequency && (
                            <>
                              <p className="text-[11px] text-gray-500 font-poppins mb-2">
                                Select {requiredWeekDayCount} day
                                {requiredWeekDayCount > 1 ? "s" : ""} for sessions
                              </p>

                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                {WEEK_DAYS.map((day) => {
                                  const selected = manualSelectedWeekDays.includes(day);
                                  const maxSelectedReached =
                                    !selected &&
                                    requiredWeekDayCount > 0 &&
                                    manualSelectedWeekDays.length >=
                                    requiredWeekDayCount;

                                  return (
                                    <label
                                      key={day}
                                      className={`inline-flex items-center gap-2 px-2 py-1 rounded-md border text-[12px] font-poppins ${selected
                                        ? "border-blue-500 bg-blue-50 text-blue-700"
                                        : "border-gray-200 text-gray-700"
                                        } ${maxSelectedReached ? "opacity-60" : ""}`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={selected}
                                        onChange={() => handleManualWeekDayToggle(day)}
                                        disabled={
                                          !isPendingStatus(selectedPatient) ||
                                          maxSelectedReached
                                        }
                                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                      />
                                      {day.slice(0, 3)}
                                    </label>
                                  );
                                })}
                              </div>
                            </>
                          )}
                        </div>

                        <div className="border border-gray-200 rounded-[12px] p-3">
                          <button
                            type="button"
                            onClick={() => setShowCalendarPreview((prev) => !prev)}
                            className="w-full flex items-center justify-between text-left px-2 py-1.5 rounded-md border border-gray-200 bg-[#F8FAFF] hover:bg-[#EEF3FF] transition-colors"
                          >
                            <span className="text-[12px] font-semibold text-[#1D1D22] font-poppins">
                              Calendar Preview
                            </span>
                            <span className="text-[11px] text-blue-600 font-poppins">
                              {showCalendarPreview ? "Hide" : "View"}
                            </span>
                          </button>

                          {showCalendarPreview && (
                            <div className="mt-2">
                              {!effectiveManualStartDate ? (
                                <p className="text-[11px] text-gray-500 font-poppins">
                                  Select start date to preview session days.
                                </p>
                              ) : isTwiceOrThriceFrequency &&
                                manualSelectedWeekDays.length !== requiredWeekDayCount ? (
                                <p className="text-[11px] text-gray-500 font-poppins">
                                  Select {requiredWeekDayCount} weekdays to calculate{" "}
                                  {effectiveSessionCount} sessions.
                                </p>
                              ) : !effectiveManualEndDate ? (
                                <p className="text-[11px] text-gray-500 font-poppins">
                                  Unable to calculate end date for selected session count.
                                </p>
                              ) : calendarPreviewMonths.length === 0 ? (
                                <p className="text-[11px] text-gray-500 font-poppins">
                                  Calendar preview is not available for selected dates.
                                </p>
                              ) : (
                                <div className="space-y-1.5">
                                  <div className="border border-[#E9EDF7] bg-[#FDFEFF] rounded-[10px] p-2">
                                    <div className="flex items-center justify-between mb-2">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          if (!canGoPrevCalendarMonth) return;
                                          const prevMonth =
                                            calendarPreviewMonths[
                                            activeCalendarMonthIndex - 1
                                            ];
                                          if (prevMonth) {
                                            setCalendarPreviewMonthKey(prevMonth.key);
                                          }
                                        }}
                                        disabled={!canGoPrevCalendarMonth}
                                        className="h-6 w-6 rounded-md border border-[#E4E9F5] text-[#6F7B92] disabled:opacity-35 disabled:cursor-not-allowed hover:bg-[#F3F6FF] flex items-center justify-center transition-colors"
                                      >
                                        <span className="text-[12px]">&lsaquo;</span>
                                      </button>

                                      <p className="text-[11px] font-semibold text-[#495267] font-poppins">
                                        {activeCalendarMonth?.label || "-"}
                                      </p>

                                      <button
                                        type="button"
                                        onClick={() => {
                                          if (!canGoNextCalendarMonth) return;
                                          const nextMonth =
                                            calendarPreviewMonths[
                                            activeCalendarMonthIndex + 1
                                            ];
                                          if (nextMonth) {
                                            setCalendarPreviewMonthKey(nextMonth.key);
                                          }
                                        }}
                                        disabled={!canGoNextCalendarMonth}
                                        className="h-6 w-6 rounded-md border border-[#E4E9F5] text-[#6F7B92] disabled:opacity-35 disabled:cursor-not-allowed hover:bg-[#F3F6FF] flex items-center justify-center transition-colors"
                                      >
                                        <span className="text-[12px]">&rsaquo;</span>
                                      </button>
                                    </div>

                                    <div className="grid grid-cols-7 gap-1 mb-1">
                                      {CALENDAR_WEEK_HEADERS.map((head) => (
                                        <div
                                          key={`calendar-head-${head}`}
                                          className="text-[9px] text-[#9AA4B7] text-center font-poppins"
                                        >
                                          {head}
                                        </div>
                                      ))}
                                    </div>

                                    <div className="grid grid-cols-7 gap-1">
                                      {activeCalendarMonth?.cells.map((cell) => {
                                        const isScheduled =
                                          previewSessionDaySet.has(cell.dateKey);

                                        return (
                                          <div
                                            key={`${activeCalendarMonth?.key}-month-cell-${cell.dateKey}`}
                                            className={`h-6 rounded-[6px] text-[10px] font-poppins flex items-center justify-center border transition-colors ${cell.inCurrentMonth
                                              ? "border-[#EDF1FA] text-[#586074] bg-white"
                                              : "border-transparent text-[#C6CCDA] bg-transparent"
                                              } ${isScheduled
                                                ? "bg-[#4361EE] text-white border-2 border-[#2F4CD6] font-semibold shadow-[0_0_0_1px_rgba(47,76,214,0.18)]"
                                                : ""
                                              }`}
                                          >
                                            {cell.label}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                  {selectedPatient &&
                    (!selectedPatient.dutyInfo ||
                      selectedPatient.dutyInfo.length === 0) &&
                    isPhysiotherapist && (
                      <div className="mt-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="border border-gray-200 rounded-[12px] p-2">
                            <p className="text-[13px] font-semibold text-[#1D1D22] font-poppins mb-2 whitespace-nowrap">
                              {dutyLabel}
                            </p>

                            {isRequestedStatus(selectedPatient) ? (
                              <div className="flex items-center gap-2 px-2 py-1.5">
                                <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center">
                                  <img
                                    src={CommonImages.nurseNew}
                                    alt="Avatar"
                                    className="w-6 h-6 object-cover rounded-full"
                                  />
                                </div>
                                <span className="text-[12px] text-[#4361EE] font-medium font-poppins truncate max-w-[150px]">
                                  {getAssignedHCPName(selectedPatient)}
                                </span>
                              </div>
                            ) : (
                              <div className="relative flex-1">
                                <select
                                  value={selectedMainStaff || ""}
                                  onChange={(e) => {
                                    handleSingleStaffChange(e.target.value);
                                  }}
                                  onFocus={async () => {
                                    if (assignStaffList.length === 0) {
                                      await getNames();
                                    }
                                  }}
                                  disabled={!isPendingStatus(selectedPatient)}
                                  className={`w-full bg-[#F6F8FC] rounded-md px-2 py-1.5 text-[12px] font-poppins appearance-none cursor-pointer focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 border border-gray-300 ${!isPendingStatus(selectedPatient)
                                    ? "opacity-50 cursor-not-allowed"
                                    : ""
                                    }`}
                                >
                                  <option value="" hidden>
                                    {isPhysioNoAssessment
                                      ? "Select Standby Staff 1"
                                      : "Select assigned HCP"}
                                  </option>

                                  {assignStaffList
                                    .filter((staff) => staff._id !== selectedStandby1)
                                    .map((staff) => (
                                      <option key={staff._id} value={staff._id}>
                                        {getStaffDisplayName(staff)}
                                      </option>
                                    ))}
                                </select>

                                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                                  <svg
                                    className="fill-current h-3 w-3"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 20 20"
                                  >
                                    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          <div className="border border-gray-200 rounded-[12px] p-2">
                            <p className="text-[13px] font-semibold text-[#1D1D22] font-poppins mb-2 whitespace-nowrap">
                              {physioStandbyLabel}
                            </p>

                            {isRequestedStatus(selectedPatient) ? (
                              <div className="flex items-center gap-2 px-2 py-1.5">
                                <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center">
                                  <img
                                    src={CommonImages.nurseNew}
                                    alt="Avatar"
                                    className="w-6 h-6 object-cover rounded-full"
                                  />
                                </div>
                                <span className="text-[12px] text-[#4361EE] font-medium font-poppins truncate max-w-[150px]">
                                  {getStandbyHCPName(selectedPatient)}
                                </span>
                              </div>
                            ) : (
                              <div className="relative flex-1">
                                <select
                                  value={selectedStandby1 || ""}
                                  onChange={(e) => {
                                    setSelectedStandby1(e.target.value);
                                    setIsEditingStaff(true);
                                  }}
                                  onFocus={async () => {
                                    if (assignStaffList.length === 0) {
                                      await getNames();
                                    }
                                  }}
                                  disabled={!isPendingStatus(selectedPatient)}
                                  className={`w-full bg-[#F6F8FC] rounded-md px-2 py-1.5 text-[12px] font-poppins appearance-none cursor-pointer focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 border border-gray-300 ${!isPendingStatus(selectedPatient)
                                    ? "opacity-50 cursor-not-allowed"
                                    : ""
                                    }`}
                                >
                                  <option value="" hidden>
                                    {isPhysioNoAssessment
                                      ? "Select Standby Staff 2"
                                      : "Select standby staff"}
                                  </option>

                                  {assignStaffList
                                    .filter(
                                      (staff) =>
                                        staff._id !== selectedMainStaff &&
                                        staff._id !== assessmentStaff
                                    )
                                    .map((staff) => (
                                      <option key={staff._id} value={staff._id}>
                                        {getStaffDisplayName(staff)}
                                      </option>
                                    ))}
                                </select>

                                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                                  <svg
                                    className="fill-current h-3 w-3"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 20 20"
                                  >
                                    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                  {selectedPatient &&
                    (!selectedPatient.dutyInfo ||
                      selectedPatient.dutyInfo.length === 0) &&
                    !isPhysiotherapist && (
                      <div className="mt-4">
                        <h4 className="text-[14px] font-semibold text-gray-800 mb-3 whitespace-nowrap">
                          Standby Staff
                        </h4>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="border border-gray-200 rounded-[12px] p-2">
                            <p className="text-[13px] font-semibold text-[#1D1D22] font-poppins mb-2 whitespace-nowrap">
                              {standby1Label}
                            </p>

                            {isRequestedStatus(selectedPatient) ? (
                              <div className="flex items-center gap-2 px-2 py-1.5">
                                <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center">
                                  <img
                                    src={CommonImages.nurseNew}
                                    alt="Avatar"
                                    className="w-6 h-6 object-cover rounded-full"
                                  />
                                </div>
                                <span className="text-[12px] text-[#4361EE] font-medium font-poppins truncate max-w-[150px]">
                                  {getStandbyHCPName(selectedPatient)}
                                </span>
                              </div>
                            ) : (
                              <div className="relative flex-1">
                                <select
                                  value={selectedStandby1 || ""}
                                  onChange={(e) => {
                                    setSelectedStandby1(e.target.value);
                                    setIsEditingStaff(true);
                                  }}
                                  onFocus={async () => {
                                    if (assignStaffList.length === 0) {
                                      await getNames();
                                    }
                                  }}
                                  disabled={!isPendingStatus(selectedPatient)}
                                  className={`w-full bg-[#F6F8FC] rounded-md px-2 py-1.5 text-[12px] font-poppins appearance-none cursor-pointer focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 border border-gray-300 ${!isPendingStatus(selectedPatient)
                                    ? "opacity-50 cursor-not-allowed"
                                    : ""
                                    }`}
                                >
                                  <option value="" hidden>
                                    Select standby staff
                                  </option>

                                  {assignStaffList
                                    .filter(
                                      (staff) =>
                                        staff._id !== selectedMainStaff &&
                                        staff._id !== selectedStandby2
                                    )
                                    .map((staff) => (
                                      <option key={staff._id} value={staff._id}>
                                        {getStaffDisplayName(staff)}
                                      </option>
                                    ))}
                                </select>

                                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                                  <svg
                                    className="fill-current h-3 w-3"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 20 20"
                                  >
                                    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>

                          <div className="border border-gray-200 rounded-[12px] p-2">
                            <p className="text-[13px] font-semibold text-[#1D1D22] font-poppins mb-2 whitespace-nowrap">
                              {standby2Label}
                            </p>

                            {isRequestedStatus(selectedPatient) ? (
                              <div className="flex items-center gap-2 px-2 py-1.5">
                                <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center">
                                  <img
                                    src={CommonImages.nurseNew}
                                    alt="Avatar"
                                    className="w-6 h-6 object-cover rounded-full"
                                  />
                                </div>
                                <span className="text-[12px] text-[#4361EE] font-medium font-poppins truncate max-w-[150px]">
                                  {selectedPatient?.backupSpecialist?.name || "N/A"}
                                </span>
                              </div>
                            ) : (
                              <div className="relative flex-1">
                                <select
                                  value={selectedStandby2 || ""}
                                  onChange={(e) => {
                                    setSelectedStandby2(e.target.value);
                                    setIsEditingStaff(true);
                                  }}
                                  onFocus={async () => {
                                    if (assignStaffList.length === 0) {
                                      await getNames();
                                    }
                                  }}
                                  disabled={!isPendingStatus(selectedPatient)}
                                  className={`w-full bg-[#F6F8FC] rounded-md px-2 py-1.5 text-[12px] font-poppins appearance-none cursor-pointer focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 border border-gray-300 ${!isPendingStatus(selectedPatient)
                                    ? "opacity-50 cursor-not-allowed"
                                    : ""
                                    }`}
                                >
                                  <option value="" hidden>
                                    Select standby staff
                                  </option>

                                  {assignStaffList
                                    .filter(
                                      (staff) =>
                                        staff._id !== selectedMainStaff &&
                                        staff._id !== selectedStandby1
                                    )
                                    .map((staff) => (
                                      <option key={staff._id} value={staff._id}>
                                        {getStaffDisplayName(staff)}
                                      </option>
                                    ))}
                                </select>

                                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                                  <svg
                                    className="fill-current h-3 w-3"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 20 20"
                                  >
                                    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                </div>

                {selectedPatient &&
                  (!selectedPatient.dutyInfo ||
                    selectedPatient.dutyInfo.length === 0) &&
                  isPendingStatus(selectedPatient) &&
                  isEditingStaff && (
                    <div className="pt-2 mt-2 border-t flex justify-end gap-3">
                      <button
                        onClick={handleCancelStaffAssignment}
                        className="px-4 py-2 text-[12px] font-poppins bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                      >
                        Cancel
                      </button>

                      <button
                        onClick={handleSaveStaffAssignment}
                        disabled={
                          !selectedMainStaff ||
                          !selectedStandby1 ||
                          !isManualVisitInfoValid
                        }
                        className="px-4 py-2 text-[12px] font-poppins bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                      >
                        Assign
                      </button>
                    </div>
                  )}
              </>
            )}
          </div>
        </div>

        <PatientHistory
          selectedPatient={selectedPatient}
          dischargeSummaries={dischargeSummariesForHistory}
        />
      </div>

      {timeModalOpen && dutyForModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-md shadow-xl p-4">
            <h3 className="text-[16px] font-semibold font-poppins text-gray-800 mb-3">
              Change Time Slot
            </h3>

            <p className="text-[12px] text-gray-500 font-poppins mb-3">
              {moment(dutyForModal.date).format("DD/MM/YYYY - dddd")}
            </p>

            <select
              value={timeModalSelectedSlot}
              onChange={(e) => setTimeModalSelectedSlot(e.target.value)}
              className="w-full bg-[#F6F8FC] rounded-md px-3 py-2 text-[12px] font-poppins border border-gray-300 outline-none"
            >
              {PHYSIO_TIME_SLOTS.map((slot) => (
                <option key={slot} value={slot}>
                  {slot}
                </option>
              ))}
            </select>

            <div className="pt-4 mt-4 border-t flex justify-end gap-3">
              <button
                onClick={closeTimeModal}
                className="px-4 py-2 text-[12px] font-poppins bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>

              <button
                onClick={applySelectedTimeSlot}
                className="px-4 py-2 text-[12px] font-poppins bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {showTracking && trackingData ? (
        <LiveTrackingMap
          open={showTracking}
          onClose={() => setShowTracking(false)}
          trackingData={trackingData}
        />
      ) : null}
    </div>
  );
};

export default PatientList;
